import React, { useEffect, useState, useRef } from 'react';
import { FormControl, ProgressBar } from 'react-bootstrap';
import { Link45deg, CameraVideo, X, Upload } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { API_URL, ENDPOINTS } from '../../helpers/constants';

const ACTIVE_COLOR = '#8B1538';
const BUTTON_STYLES = {
  base: {
    fontSize: 10,
    fontWeight: 500,
    padding: '1px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    transition: 'all 0.2s',
    minHeight: '20px'
  },
  active: {
    backgroundColor: ACTIVE_COLOR,
    color: 'white',
    boxShadow: '0 1px 2px rgba(139, 21, 56, 0.2)'
  },
  inactive: {
    backgroundColor: 'transparent',
    color: '#6c757d',
    boxShadow: 'none'
  }
};

const VirtualTourInput = field => {
  const { preValue, disabled, containerClassName = '', onVirtualTourUploadBlockingChange } = field;

  const linkPreValue = preValue?.linkPreValue ?? '';
  const videoPreValue = preValue?.videoPreValue ?? [];

  const getInitialMode = () => {
    if (videoPreValue && Array.isArray(videoPreValue) && videoPreValue.length > 0) return 'video';
    if (linkPreValue?.trim()) return 'link';
    return 'link';
  };

  const [mode, setMode] = useState(getInitialMode());
  const [linkValue, setLinkValue] = useState(linkPreValue || '');
  const [videos, setVideos] = useState(
    Array.isArray(videoPreValue) ? videoPreValue : videoPreValue ? [videoPreValue] : []
  );
  const [breakoutStyles, setBreakoutStyles] = useState({});

  const videoInputRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const currentVideo = videos[0] || null;
  const videoFilename = currentVideo?.filename || currentVideo?.name ||
    (currentVideo?.url ? currentVideo.url.split('/').pop() : '') || '';
  const showUploadProgress =
    currentVideo && ['WAITING_FOR_UPLOAD', 'UPLOADING'].includes(currentVideo.status);
  const showFullWidthSection =
    currentVideo &&
    (showUploadProgress || currentVideo.status === 'UPLOADED');
  const blocksSubmitForVideo =
    mode === 'video' &&
    currentVideo &&
    ['WAITING_FOR_UPLOAD', 'UPLOADING'].includes(currentVideo.status);
  const hiddenVideoJson = videos && videos.length ? JSON.stringify(videos) : '';

  useEffect(() => {
    if (!onVirtualTourUploadBlockingChange) return;
    onVirtualTourUploadBlockingChange(!!blocksSubmitForVideo);
    return () => onVirtualTourUploadBlockingChange(false);
  }, [blocksSubmitForVideo, onVirtualTourUploadBlockingChange]);

  useEffect(() => {
    const link = preValue?.linkPreValue ?? '';
    const video = preValue?.videoPreValue ?? [];
    setLinkValue(link || '');
    setVideos(Array.isArray(video) ? video : video ? [video] : []);
    if (video && Array.isArray(video) && video.length > 0) {
      setMode('video');
    } else if (link?.trim()) {
      setMode('link');
    }
  }, [preValue]);

  useEffect(() => {
    const pendingVideo = videos[0];
    if (!pendingVideo?.file || pendingVideo.status !== 'WAITING_FOR_UPLOAD') return;
    let cancelled = false;
    const { id, file } = pendingVideo;
    (async () => {
      setVideos(prev => {
        const cur = prev[0];
        if (!cur || cur.id !== id) return prev;
        return [{ ...cur, status: 'UPLOADING' }];
      });
      if (cancelled) return;
      const formData = new FormData();
      formData.append('video', file);
      const { error, response } = await makeApiRequests({
        requestBody: formData,
        endpoint: ENDPOINTS.DISK_VIDEO_UPLOAD,
        method: 'POST',
        stringify: false
      });
      if (cancelled) return;
      if (error) {
        toast.error('Failed to upload video');
        setVideos(prev => {
          const cur = prev[0];
          if (!cur || cur.id !== id) return prev;
          return [{ ...cur, status: 'ERROR' }];
        });
        return;
      }
      toast.success('Video uploaded successfully');
      setVideos(prev => {
        const cur = prev[0];
        if (!cur || cur.id !== id) return prev;
        const { file: _discard, ...clean } = { ...cur, ...response, status: 'UPLOADED' };
        return [clean];
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [videos[0]?.id]);

  const getVideoUrl = video => {
    if (!video) return null;
    if (video.status === 'UPLOADED' && video.filename) {
      const baseUrl = API_URL || window.location.origin;
      return `${baseUrl.replace(/\/$/, '')}${ENDPOINTS.PUBLIC_VIDEO(video.filename)}`;
    }
    if (video.url) {
      const baseUrl = API_URL || window.location.origin;
      return video.url.startsWith('http') ? video.url : `${baseUrl.replace(/\/$/, '')}${ENDPOINTS.PUBLIC_VIDEO(video.url)}`;
    }
    return video.previewUrl;
  };

  const handleVideoClose = () => {
    if (currentVideo?.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(currentVideo.previewUrl);
    }
    setVideos([]);
  };

  const handleFileSelect = e => {
    const file = e.target?.files?.[0];
    if (file?.type.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setVideos([{
        file,
        status: 'WAITING_FOR_UPLOAD',
        id: `video-${Date.now()}`,
        previewUrl,
        filename: file.name,
        name: file.name
      }]);
    }
    if (e.target) e.target.value = '';
  };

  const isColElement = element => {
    if (!element?.classList) return false;
    const classList = Array.from(element.classList);
    return classList.some(cls =>
      cls.startsWith('col-') || cls === 'col' ||
      cls.startsWith('col-xs') || cls.startsWith('col-sm') ||
      cls.startsWith('col-md') || cls.startsWith('col-lg') ||
      cls.startsWith('col-xl')
    );
  };

  const findRowOrContainer = (colElement, formContainer) => {
    let rowParent = colElement.parentElement;
    while (rowParent && rowParent !== document.body) {
      const rowStyle = window.getComputedStyle(rowParent);
      if (rowParent.classList.contains('row') || rowStyle.display === 'flex' || rowParent.tagName === 'ROW') {
        return {
          element: rowParent,
          width: rowParent.getBoundingClientRect().width,
          offset: colElement.getBoundingClientRect().left - rowParent.getBoundingClientRect().left
        };
      }
      rowParent = rowParent.parentElement;
    }

    if (formContainer) {
      const containerRect = formContainer.getBoundingClientRect();
      return {
        element: formContainer,
        width: containerRect.width,
        offset: colElement.getBoundingClientRect().left - containerRect.left
      };
    }

    return null;
  };

  useEffect(() => {
    if (!showFullWidthSection || !videoPreviewRef.current) {
      setBreakoutStyles({});
      return;
    }

    const calculateBreakout = () => {
      if (!videoPreviewRef.current) return;

      let parent = videoPreviewRef.current.parentElement;
      let colElement = null;
      let formContainer = null;

      while (parent && parent !== document.body) {
        if (parent.id === 'form-container' ||
          parent.classList.contains('container') ||
          parent.classList.contains('container-fluid')) {
          formContainer = parent;
        }
        if (isColElement(parent)) {
          colElement = parent;
        }
        parent = parent.parentElement;
      }

      if (!colElement) {
        setBreakoutStyles({});
        return;
      }

      const colRect = colElement.getBoundingClientRect();
      const colStyle = window.getComputedStyle(colElement);
      const paddingLeft = parseFloat(colStyle.paddingLeft) || 15;
      const paddingRight = parseFloat(colStyle.paddingRight) || 15;

      const rowInfo = findRowOrContainer(colElement, formContainer);
      if (!rowInfo) {
        setBreakoutStyles({});
        return;
      }

      const { width: targetWidth, offset: offsetLeft } = rowInfo;
      const contentStartOffset = offsetLeft + paddingLeft;
      const marginLeft = -contentStartOffset;
      const marginRight = -(targetWidth - offsetLeft - colRect.width + paddingRight);

      setBreakoutStyles({
        position: 'relative',
        marginLeft: `${marginLeft}px`,
        marginRight: `${marginRight}px`,
        width: `${targetWidth - paddingLeft}px`,
        maxWidth: `${targetWidth - paddingLeft}px`,
        paddingLeft: `${paddingLeft}px`,
        boxSizing: 'border-box'
      });
    };

    const timeoutId = setTimeout(calculateBreakout, 0);
    let resizeObserver = null;

    if (window.ResizeObserver && videoPreviewRef.current) {
      resizeObserver = new ResizeObserver(calculateBreakout);
      resizeObserver.observe(videoPreviewRef.current);
    }

    window.addEventListener('resize', calculateBreakout);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', calculateBreakout);
    };
  }, [showFullWidthSection, currentVideo]);

  const renderModeButton = (buttonMode, IconComponent, label) => {
    const isActive = mode === buttonMode;
    return (
      <button
        type="button"
        onClick={() => setMode(buttonMode)}
        disabled={disabled}
        className="btn border-0 flex-fill"
        style={{
          ...BUTTON_STYLES.base,
          ...(isActive ? BUTTON_STYLES.active : BUTTON_STYLES.inactive)
        }}
      >
        {React.createElement(IconComponent, { size: 12 })}
        <span>{label}</span>
      </button>
    );
  };

  const renderVideoPreview = () => {
    if (!currentVideo || currentVideo.status !== 'UPLOADED') return null;

    const videoUrl = getVideoUrl(currentVideo);
    if (!videoUrl) return null;

    return (
      <div
        className="video-preview-container"
        style={{
          position: 'relative',
          width: '100%'
        }}
      >
        <label
          className="mb-0 midFont"
          style={{
            fontSize: 'clamp(0.8rem, 2vw, 0.875rem)',
            fontWeight: 500,
            lineHeight: '1.5',
            marginBottom: '0.25rem',
            display: 'block'
          }}
        >
          Video Preview
        </label>
        <div
          style={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#000',
            borderRadius: '6px',
            overflow: 'hidden'
          }}
        >
          {!disabled && (
            <button
              type="button"
              onClick={handleVideoClose}
              className="btn btn-link p-0"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
                width: 'clamp(28px, 6vw, 32px)',
                height: 'clamp(28px, 6vw, 32px)',
                minWidth: '28px',
                minHeight: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
              }}
              aria-label="Close video preview"
            >
              <X size={16} />
            </button>
          )}

          <video
            src={videoUrl}
            controls
            playsInline
            style={{
              width: '100%',
              display: 'block',
              maxHeight: 'clamp(200px, 40vw, 250px)',
              height: 'auto',
              objectFit: 'contain'
            }}
            onError={e => {
              console.error('Video load error:', e);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={containerClassName}
      style={{ marginTop: 0, paddingTop: 0, position: 'relative', overflow: 'visible', width: '100%' }}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: '0.25rem', marginTop: 0, paddingTop: 0 }}
      >
        <label
          className="mb-0 midFont"
          style={{ fontSize: 14, fontWeight: 500, lineHeight: '1.5' }}
        >
          Virtual Tour
        </label>
        <div
          className="d-flex align-items-center flex-shrink-0"
          style={{
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            gap: '1px',
            minWidth: '120px'
          }}
        >
          {renderModeButton('link', Link45deg, 'Link')}
          {renderModeButton('video', CameraVideo, 'Video')}
        </div>
      </div>

      {mode === 'link' && (
        <div className="mb-3">
          <FormControl
            size="sm"
            type="text"
            placeholder="https://..."
            value={linkValue}
            onChange={e => setLinkValue(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}

      {mode === 'video' && (
        <>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <div>
            {currentVideo ? (
              <div
                className="d-flex align-items-center"
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  minHeight: '31px',
                  backgroundColor: '#fff'
                }}
              >
                <CameraVideo size={14} className="text-muted mr-2" style={{ flexShrink: 0 }} />
                <span
                  className="text-truncate flex-grow-1"
                  style={{ minWidth: 0, fontSize: 'clamp(11px, 2.5vw, 12px)' }}
                >
                  {videoFilename}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={handleVideoClose}
                    className="btn btn-link p-0 ml-2"
                    style={{ flexShrink: 0, color: '#6c757d' }}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => !disabled && videoInputRef.current?.click()}
                disabled={disabled}
                className="w-100 d-flex align-items-center justify-content-center"
                style={{
                  border: '1px dashed #dee2e6',
                  borderRadius: '6px',
                  padding: '5.5px 12px',
                  fontSize: 'clamp(11px, 2.5vw, 12px)',
                  minHeight: '30px',
                  color: '#6c757d',
                  backgroundColor: '#fff',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '16px'
                }}
                onMouseEnter={e => {
                  if (!disabled) {
                    e.currentTarget.style.borderColor = ACTIVE_COLOR;
                    e.currentTarget.style.color = ACTIVE_COLOR;
                  }
                }}
                onMouseLeave={e => {
                  if (!disabled) {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.color = '#6c757d';
                  }
                }}
              >
                <Upload size={14} className="mr-2" />
                Upload Video
              </button>
            )}
          </div>

          {showFullWidthSection && (
            <div
              ref={videoPreviewRef}
              className="virtual-tour-full-width-section"
              style={{
                position: 'relative',
                width: '100%',
                marginTop: '0.75rem',
                ...breakoutStyles
              }}
            >
              {showUploadProgress && (
                <div className="w-100">
                  <div className="small text-muted mb-1">Uploading to server…</div>
                  <ProgressBar animated now={100} className="w-100" style={{ height: 8, width: '100%' }} />
                </div>
              )}
              {renderVideoPreview()}
            </div>
          )}
        </>
      )}

      <input
        type="hidden"
        id="virtualTourLink"
        name="virtualTourLink"
        value={linkValue}
        readOnly
      />
      <input
        type="hidden"
        id="virtualTourVideo"
        name="virtualTourVideo"
        value={hiddenVideoJson}
        readOnly
      />
    </div>
  );
};

export default VirtualTourInput;
